import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { careTeamService } from '../../../services/careTeamService';

const FindProvider = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [selectedMode, setSelectedMode] = useState('all');
  const [selectedAvailability, setSelectedAvailability] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [providers, setProviders] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [showFilters, setShowFilters] = useState(true);
  
  // API state management
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalProviders, setTotalProviders] = useState(0);
  const [hasMoreProviders, setHasMoreProviders] = useState(false);
  const [error, setError] = useState(null);


  // Specialties list
  const specialties = [
    'Anxiety',
    'Depression',
    'Stress',
    'Relationship Issues',
    'Trauma/PTSD',
    'OCD',
    'Bipolar Disorder',
    'ADHD',
    'Addiction',
    'Eating Disorders',
    'Sleep Disorders',
    'Child & Adolescent',
    'Career Counseling',
    'Grief & Loss'
  ];

  // Load providers on component mount
  useEffect(() => {
    loadProviders(1, true);
  }, []);

  // Function to load providers from API
  const loadProviders = async (page = 1, isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }

      const response = await careTeamService.getPublicCareTeamList({
        page,
        per_page: 6
      });

      if (response.success) {
        const { items, total, page: currentPageNum, pages } = response.data;
        
        if (isInitialLoad) {
          setProviders(items);
          setFilteredProviders(items);
        } else {
          // Append new providers to existing list
          setProviders(prev => [...prev, ...items]);
          setFilteredProviders(prev => [...prev, ...items]);
        }
        
        setCurrentPage(currentPageNum);
        setTotalPages(pages);
        setTotalProviders(total);
        setHasMoreProviders(currentPageNum < pages);
      } else {
        setError(response.error);
        console.error('Failed to load providers:', response.error);
      }
    } catch (err) {
      setError({
        message: 'Failed to load providers. Please try again.',
        status: 500
      });
      console.error('Error loading providers:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Function to load more providers
  const loadMoreProviders = () => {
    if (!loadingMore && hasMoreProviders) {
      loadProviders(currentPage + 1, false);
    }
  };

  // Filter providers based on selected criteria
  useEffect(() => {
    let filtered = [...providers];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(provider =>
        provider.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.professional_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        provider.about_summary?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Specialty filter (using professional_title for now)
    if (selectedSpecialty !== 'all') {
      filtered = filtered.filter(provider =>
        provider.professional_title.toLowerCase().includes(selectedSpecialty.toLowerCase())
      );
    }

    // Location filter (using organization_name for now)
    if (selectedLocation !== 'all') {
      filtered = filtered.filter(provider =>
        provider.organization_name?.toLowerCase().includes(selectedLocation.toLowerCase())
      );
    }

    // Gender filter (not available in API response, skip for now)
    // if (selectedGender !== 'all') {
    //   filtered = filtered.filter(provider =>
    //     provider.gender.toLowerCase() === selectedGender.toLowerCase()
    //   );
    // }

    // Language filter (not available in API response, skip for now)
    // if (selectedLanguage !== 'all') {
    //   filtered = filtered.filter(provider =>
    //     provider.languages.includes(selectedLanguage)
    //   );
    // }

    // Consultation mode filter (not available in API response, skip for now)
    // if (selectedMode !== 'all') {
    //   filtered = filtered.filter(provider =>
    //     provider.consultationModes.includes(selectedMode)
    //   );
    // }

    // Availability filter
    if (selectedAvailability !== 'all') {
      if (selectedAvailability === 'today') {
        filtered = filtered.filter(provider =>
          provider.availability_status?.toLowerCase().includes('available')
        );
      } else if (selectedAvailability === 'week') {
        filtered = filtered.filter(provider =>
          provider.availability_status?.toLowerCase().includes('available')
        );
      }
    }

    // Price filter
    filtered = filtered.filter(provider =>
      provider.consultation_fee >= priceRange[0] && provider.consultation_fee <= priceRange[1]
    );

    setFilteredProviders(filtered);
  }, [searchTerm, selectedSpecialty, selectedLocation, selectedGender, 
      selectedLanguage, selectedMode, selectedAvailability, priceRange, providers]);

  // Provider Card Component
  const ProviderCard = ({ provider }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02 }}
      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="text-5xl">👩‍⚕️</div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-900">{provider.user_name}</h3>
                {provider.is_public && (
                  <span className="text-blue-500 text-sm" title="Verified">✓</span>
                )}
              </div>
              <p className="text-sm text-gray-600">{provider.professional_title}</p>
              <p className="text-xs text-gray-500">{provider.experience_years} years experience</p>
            </div>
          </div>
          <button 
            onClick={() => navigate(`/booking/quick-booking?provider=${provider.user_id}`)}
            className="text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full hover:bg-green-200 transition-colors"
          >
            Quick Book
          </button>
        </div>

        {/* Rating and Reviews */}
        <div className="flex items-center mb-3 text-sm">
          <div className="flex items-center">
            <span className="text-yellow-500">★</span>
            <span className="ml-1 font-semibold">{provider?.rating || 'N/A'}</span>
          </div>
          <span className="text-gray-500 ml-2">({provider.total_reviews || 0} reviews)</span>
        </div>

        {/* Organization */}
        {provider.organization_name && (
          <div className="mb-4">
            <div className="flex items-center text-sm text-gray-600">
              <span className="mr-2">🏢</span>
              <span>{provider.organization_name}</span>
            </div>
          </div>
        )}

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          {/* <div className="flex items-center text-gray-600">
            <span className="mr-2">📧</span>
            <span className="truncate">{provider.user_email}</span>
          </div> */}
          <div className="flex items-center text-gray-600">
            <span className="mr-2">📱</span>
            <span className="truncate">{provider.user_mobile}</span>
          </div>
          <div className="flex items-center text-gray-600">
            <span className="mr-2">💰</span>
            <span>₹{provider.consultation_fee}/session</span>
          </div>
          <div className="flex items-center text-gray-600">
            <span className="mr-2">📅</span>
            <span className="truncate">{provider.availability_status}</span>
          </div>
        </div>

        {/* About */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {provider.about_summary || 'No description available'}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/booking/provider/${provider.user_id}`, { 
              state: { provider: provider } 
            })}
            className="flex-1 bg-white border border-blue-500 text-blue-500 py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
          >
            View Profile
          </button>
          <button
            onClick={() => navigate(`/booking/book-appointment?provider=${provider.user_id}`, { 
              state: { provider: provider } 
            })}
            className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
          >
            Book Appointment
          </button>
        </div>
      </div>
    </motion.div>
  );

  // List View Component
  const ProviderListItem = ({ provider }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-6 mb-4"
    >
      <div className="flex flex-col md:flex-row gap-6">
        {/* Left Section - Provider Info */}
        <div className="flex-1">
          <div className="flex items-start gap-4">
            <div className="text-5xl">👩‍⚕️</div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xl font-semibold text-gray-900">{provider.user_name}</h3>
                {provider.is_public && (
                  <span className="text-blue-500" title="Verified">✓</span>
                )}
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                  {provider.availability_status}
                </span>
              </div>
              <p className="text-gray-600 mb-1">{provider.professional_title} • {provider.experience_years} years</p>
              
              {/* Rating */}
              <div className="flex items-center mb-3">
                <span className="text-yellow-500">★</span>
                <span className="ml-1 font-semibold">{provider.rating || 'N/A'}</span>
                <span className="text-gray-500 ml-2">({provider.total_reviews || 0} reviews)</span>
              </div>

              {/* Organization */}
              {provider.organization_name && (
                <div className="mb-3">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">🏢</span>
                    <span>{provider.organization_name}</span>
                  </div>
                </div>
              )}

              {/* About */}
              <p className="text-sm text-gray-600 mb-3">{provider.about_summary || 'No description available'}</p>

              {/* Additional Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                {/* <div className="flex items-center">
                  <span className="mr-2">📧</span>
                  <span className="truncate">{provider.user_email}</span>
                </div> */}
                <div className="flex items-center">
                  <span className="mr-2">📱</span>
                  <span>{provider.user_mobile}</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">💰</span>
                  <span>₹{provider.consultation_fee}</span>
                </div>
                <div className="flex items-center">
                  <span className="mr-2">📅</span>
                  <span>{provider.availability_status}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex md:flex-col gap-2 md:w-48">
          <button
            onClick={() => navigate(`/booking/provider/${provider.user_id}`, { 
              state: { provider: provider } 
            })}
            className="flex-1 bg-white border border-blue-500 text-blue-500 py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
          >
            View Profile
          </button>
          <button
            onClick={() => navigate(`/booking/book-appointment?provider=${provider.user_id}`, { 
              state: { provider: provider } 
            })}
            className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
          >
            Book Now
          </button>
          <button
            onClick={() => navigate(`/booking/quick-booking?provider=${provider.user_id}`, { 
              state: { provider: provider } 
            })}
            className="flex-1 bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors text-sm font-medium md:hidden lg:block"
          >
            Quick Book
          </button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-8 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Find Your Mental Health Provider</h1>
          <p className="text-lg opacity-90">Connect with licensed therapists, psychiatrists, and counselors</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔍
                </span>
                <input
                  type="text"
                  placeholder="Search by name, specialty, or condition..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium flex items-center gap-2 md:hidden lg:flex"
              >
                <span>🎯</span>
                {showFilters ? 'Hide' : 'Show'} Filters
              </button>
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-4 py-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
                  title="Grid View"
                >
                  ⊞
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-4 py-2 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
                  title="List View"
                >
                  ☰
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:w-80"
            >
              <div className="bg-white rounded-xl shadow-md p-6 sticky top-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center justify-between">
                  Filters
                  <button
                    onClick={() => {
                      setSelectedSpecialty('all');
                      setSelectedLocation('all');
                      setSelectedGender('all');
                      setSelectedLanguage('all');
                      setSelectedMode('all');
                      setSelectedAvailability('all');
                      setPriceRange([0, 5000]);
                    }}
                    className="text-sm text-blue-500 hover:text-blue-600"
                  >
                    Clear All
                  </button>
                </h2>

                {/* Specialty Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specialty
                  </label>
                  <select
                    value={selectedSpecialty}
                    onChange={(e) => setSelectedSpecialty(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Specialties</option>
                    {specialties.map(specialty => (
                      <option key={specialty} value={specialty}>
                        {specialty}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Locations</option>
                    <option value="kochi">Kochi</option>
                    <option value="ernakulam">Ernakulam</option>
                    <option value="thrissur">Thrissur</option>
                    <option value="trivandrum">Trivandrum</option>
                    <option value="kozhikode">Kozhikode</option>
                    <option value="kannur">Kannur</option>
                  </select>
                </div>

                {/* Gender Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Provider Gender
                  </label>
                  <select
                    value={selectedGender}
                    onChange={(e) => setSelectedGender(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">No Preference</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                {/* Language Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Language
                  </label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Languages</option>
                    <option value="English">English</option>
                    <option value="Hindi">Hindi</option>
                    <option value="Malayalam">Malayalam</option>
                    <option value="Tamil">Tamil</option>
                    <option value="Arabic">Arabic</option>
                  </select>
                </div>

                {/* Consultation Mode Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Consultation Mode
                  </label>
                  <select
                    value={selectedMode}
                    onChange={(e) => setSelectedMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Modes</option>
                    <option value="video">Video Consultation</option>
                    <option value="in-person">In-Person</option>
                    <option value="phone">Phone</option>
                  </select>
                </div>

                {/* Availability Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Availability
                  </label>
                  <select
                    value={selectedAvailability}
                    onChange={(e) => setSelectedAvailability(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Any Time</option>
                    <option value="today">Available Today</option>
                    <option value="week">This Week</option>
                  </select>
                </div>

                {/* Price Range Filter */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price Range (₹{priceRange[0]} - ₹{priceRange[1]})
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="5000"
                    step="100"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="w-full"
                  />
                </div>

                {/* Quick Actions */}
                <div className="border-t pt-4">
                  <Link
                    to="/booking/quick-booking"
                    className="block w-full text-center bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 transition-colors font-medium mb-2"
                  >
                    🚀 Quick Booking
                  </Link>
                  <Link
                    to="/emergency"
                    className="block w-full text-center bg-red-500 text-white py-2 px-4 rounded-lg hover:bg-red-600 transition-colors font-medium"
                  >
                    🆘 Emergency Support
                  </Link>
                </div>
              </div>
            </motion.div>
          )}

          {/* Results Section */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-600">
                Found <span className="font-semibold text-gray-900">{filteredProviders.length}</span> of <span className="font-semibold text-gray-900">{totalProviders}</span> providers
              </p>
              <select className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option>Sort by: Relevance</option>
                <option>Sort by: Rating</option>
                <option>Sort by: Price (Low to High)</option>
                <option>Sort by: Price (High to Low)</option>
                <option>Sort by: Experience</option>
              </select>
            </div>

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
                <div className="flex items-center mb-4">
                  <div className="text-red-500 text-2xl mr-3">⚠️</div>
                  <div>
                    <h3 className="text-lg font-semibold text-red-800">Error Loading Providers</h3>
                    <p className="text-red-600">{error.message}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => loadProviders(1, true)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={() => setError(null)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            )}

            {/* No Results */}
            {!loading && !error && filteredProviders.length === 0 && (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <p className="text-xl text-gray-600 mb-4">No providers found matching your criteria</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedSpecialty('all');
                    setSelectedLocation('all');
                  }}
                  className="text-blue-500 hover:text-blue-600 font-medium"
                >
                  Clear filters and try again
                </button>
              </div>
            )}

            {/* Results Grid/List */}
            {!loading && filteredProviders.length > 0 && (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredProviders.map(provider => (
                      <ProviderCard key={provider.id} provider={provider} />
                    ))}
                  </div>
                ) : (
                  <div>
                    {filteredProviders.map(provider => (
                      <ProviderListItem key={provider.id} provider={provider} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Load More */}
            {!loading && !error && filteredProviders.length > 0 && hasMoreProviders && (
              <div className="text-center mt-8">
                <button 
                  onClick={loadMoreProviders}
                  disabled={loadingMore}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingMore ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading More...
                    </div>
                  ) : (
                    'Load More Providers'
                  )}
                </button>
                <p className="text-sm text-gray-500 mt-2">
                  Showing {filteredProviders.length} of {totalProviders} providers
                </p>
              </div>
            )}

            {/* No More Providers */}
            {!loading && !error && filteredProviders.length > 0 && !hasMoreProviders && (
              <div className="text-center mt-8">
                <p className="text-gray-500">You've reached the end of the list</p>
                <p className="text-sm text-gray-400 mt-1">
                  Showing all {totalProviders} providers
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8 mt-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Need Help Finding the Right Provider?
            </h2>
            <p className="text-gray-600 mb-6">
              Our care coordinators can help match you with the perfect mental health professional based on your specific needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/contact')}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                📞 Call Us: +1 (888) 404-7470
              </button>
              <button
                onClick={() => navigate('/self-help')}
                className="px-6 py-3 bg-white text-blue-500 border border-blue-500 rounded-lg hover:bg-blue-50 transition-colors font-medium"
              >
                📋 Take Assessment First
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindProvider;